import { Box, Grid } from '@mui/material';
import { State } from 'app/core/reducers';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import {
	handleSubmit,
	prefillProfileEditForm,
	setEmailAddress,
	setFirstName,
	setLastName,
	setTelephone
} from './actions';
import { RouteLeavingGuard } from '../../components/RouteLeavingGuard';
import { useHistory } from 'react-router';
import Page from '../../mui-custom/Page/Page';
import ActionBar from '../../mui-custom/ActionBar/ActionBar';
import {
	AdminExternalProfileAppV3,
	type AdminExternalProfileAppV3Handle
} from '../admin/externalProfile/AppV3';

export const ProfileForm = () => {
	const profileFormId = 'profile-form';
	const { firstName, lastName, telephone, email } = useSelector(
		(state: State) => state?.profileEdit
	);
	const { user } = useSelector((state: State) => state.auth);
	const { t } = useTranslation();
	const history = useHistory();
	const dispatch = useDispatch();
	const [isEditing, setIsEditing] = useState(false);
	const [isExternalProfileEditing, setIsExternalProfileEditing] = useState(false);
	const externalProfileRef = useRef<AdminExternalProfileAppV3Handle>(null);

	useEffect(() => {
		dispatch(
			prefillProfileEditForm({
				user
			})
		);
	}, []);

	const onSubmit = async () => {
		setIsEditing(false);
		// eslint-disable-next-line @typescript-eslint/await-thenable
		await dispatch(handleSubmit());
	};

	const handleSave = async () => {
		const profileIsValid = !isEditing || (await formRef.current?.isFormValid?.(true));
		const externalProfileIsValid =
			!isExternalProfileEditing || (await externalProfileRef.current?.isValid());

		if (!profileIsValid || !externalProfileIsValid) {
			return;
		}

		if (isEditing) {
			await onSubmit();
		}

		if (isExternalProfileEditing) {
			await externalProfileRef.current?.submit();
		}
	};

	const handleDiscard = () => {
		if (isEditing) {
			setIsEditing(false);
			dispatch(
				prefillProfileEditForm({
					user
				})
			);
		}

		if (isExternalProfileEditing) {
			externalProfileRef.current?.reset();
		}
	};

	const handleFocus = (_ev: Event) => {
		setIsEditing(true);
	};
	const formRef = useRef(null);

	return (
		<Page
			headline={t('general:profile')}
			actionBar={
				(isEditing || isExternalProfileEditing) && (
					<ActionBar
						primary={{
							children: t('general:cta_save_edit'),
							onClick: handleSave
						}}
						primaryAsSecondary
						secondary={{
							children: t('general:cta_discard'),
							onClick: handleDiscard
						}}
					/>
				)
			}
		>
			<ValidatorForm
				id={profileFormId}
				ref={formRef}
				onSubmit={onSubmit}
				onError={(errors) => console.log(errors)}
				onFocus={handleFocus}
			>
				<Box>
					<Grid container spacing={2}>
						<Grid item {...{ xs: 12, mobile: 6 }}>
							<TextValidator
								name="profileFirstNameInput"
								label={t('general:first_name')}
								validators={['required']}
								value={firstName}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									dispatch(setFirstName(e.target.value))
								}
								errorMessages={[t('general:required_message')]}
								fullWidth
							/>
						</Grid>
						<Grid item {...{ xs: 12, mobile: 6 }}>
							<TextValidator
								label={t('general:last_name')}
								name="profileLastNameInput"
								validators={['required']}
								value={lastName}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									dispatch(setLastName(e.target.value))
								}
								errorMessages={[t('general:required_message')]}
								fullWidth
							/>
						</Grid>
						<Grid item {...{ xs: 12 }}>
							<TextValidator
								label={t('general:telephone')}
								name="profilePhoneInput"
								value={telephone}
								type="telephone"
								validators={['isValidPhoneNumber']}
								errorMessages={[
									t('general:required_message'),
									t('validation:invalid_phone_number')
								]}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									dispatch(setTelephone(e.target.value))
								}
								fullWidth
							/>
						</Grid>
						<Grid item {...{ xs: 12 }}>
							<TextValidator
								label={t('general:email')}
								name="profileEMailInput"
								validators={['required', 'isEmail', 'isEmailChangedAndAvailable']}
								value={email}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									dispatch(setEmailAddress(e.target.value))
								}
								errorMessages={[
									t('general:required_message'),
									t('validation:invalid_email_address'),
									t('validation:email_already_exists')
								]}
								fullWidth
							/>
						</Grid>
					</Grid>
				</Box>
				<RouteLeavingGuard
					navigate={(path) => history.push(path)}
					shouldBlockNavigation={(_location) => isEditing}
					title={t('profile:discard_changes')}
					confirmButtonText={t('general:cta_discard_edit')}
				/>
			</ValidatorForm>
			<AdminExternalProfileAppV3
				ref={externalProfileRef}
				onEditingChange={setIsExternalProfileEditing}
			/>
		</Page>
	);
};
